import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import PageBanner from "../../components/common/PageBanner";
import {
  FaCheckCircle,
  FaCalendarAlt,
  FaUsers,
  FaTrophy,
  FaFileContract,
  FaShieldAlt,
  FaUser,
  FaUserFriends,
  FaPlus,
  FaTrash,
  FaEraser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaSpinner,
  FaArrowRight,
} from "react-icons/fa";

const DEFAULT_BANNER =
  "https://res.cloudinary.com/dqxlte6wf/image/upload/v1763807009/vmn2cg8pq8gyhzcgakvl.png";

// Helper to normalize any incoming package string into our 4 canonical package names
export const resolveCanonicalPackage = (pkgString) => {
  if (!pkgString) return "All-Star Squad Party";
  const str = String(pkgString).toLowerCase().trim();
  if (str.includes("small")) return "Small Squad Party";
  if (
    str.includes("all-star") ||
    str.includes("all star") ||
    str.includes("allstar")
  )
    return "All-Star Squad Party";
  if (str.includes("big") || str.includes("league")) return "Big League Party";
  if (str.includes("large") || str.includes("custom") || str.includes("25"))
    return "Large Group (25+)";
  return "All-Star Squad Party";
};

const PartyRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Pre-populated state passed from package cards, query parameters, or defaults
  const searchParams = new URLSearchParams(location.search);
  const rawParamPackage = searchParams.get("package");
  const rawStatePackage = location.state?.selectedPackage;
  const initialPackage = resolveCanonicalPackage(
    rawStatePackage || rawParamPackage || "All-Star Squad Party",
  );

  const rawSport =
    location.state?.selectedSport ||
    searchParams.get("sport") ||
    "Indoor Soccer";
  const initialSport = initialPackage.includes("Small")
    ? "Indoor Soccer"
    : rawSport.includes("Dual")
      ? "Indoor Soccer"
      : rawSport;

  const rawPlayers = location.state?.players
    ? Number(location.state.players)
    : initialPackage.includes("Small")
      ? 10
      : initialPackage.includes("All-Star")
        ? 15
        : initialPackage.includes("Big")
          ? 20
          : 26;
  const initialPlayers = initialPackage.includes("Small")
    ? Math.min(rawPlayers, 12)
    : rawPlayers;

  const preSelectedFood = location.state?.selectedFood || [];
  const preSelectedAddons = location.state?.selectedAddons || [];

  const [bannerBg, setBannerBg] = useState(DEFAULT_BANNER);

  // Signee Type: "adult" or "adult_with_dependants"
  const [signeeType, setSigneeType] = useState("adult_with_dependants");

  // Attendance option when Adult with Dependants is selected
  const [attendanceOption, setAttendanceOption] = useState("both"); // "both" | "dependants_only"

  // Primary Signee Details
  const [signeeData, setSigneeData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobilePhone: "",
    dob: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "GA",
    zipCode: "",
  });

  // Dynamic Dependants list
  const [dependants, setDependants] = useState([
    {
      firstName: "",
      lastName: "",
      email: "",
      mobilePhone: "",
      dob: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "GA",
      zipCode: "",
    },
  ]);

  // Party / Event Configuration
  const [eventData, setEventData] = useState({
    selectedPackage: initialPackage,
    selectedSport: initialSport,
    eventDate: "",
    timeSlot: "1:00 PM - 3:30 PM",
    participants: initialPlayers,
    celebrantName: "",
    celebrantAge: "",
    notes: "",
  });

  // Add-ons & Food Selection
  const [selectedAddons, setSelectedAddons] = useState(preSelectedAddons);
  const [selectedFood, setSelectedFood] = useState(preSelectedFood);

  // Legal & Consent
  const [electronicConsent, setElectronicConsent] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedWaiver, setAgreedWaiver] = useState(false);

  // Signature Canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingReference, setBookingReference] = useState("");
  const [activeDbPackages, setActiveDbPackages] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchBanner = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/page-banner/get-banner/rental`,
        );
        if (res?.data?.bgImage) setBannerBg(res.data.bgImage);
      } catch (e) {
        console.log("Error loading banner", e);
      }
    };

    const fetchActivePackages = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/event-packages/content`,
        );
        if (res?.data?.success && res.data.packages) {
          setActiveDbPackages(res.data.packages);
        }
      } catch (e) {
        console.log("Error loading packages", e);
      }
    };

    fetchBanner();
    fetchActivePackages();
  }, []);

  // Synchronize immediately whenever navigating from event package cards or query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const navPackage = location.state?.selectedPackage || params.get("package");
    if (navPackage) {
      const canonical = resolveCanonicalPackage(navPackage);
      let sport =
        location.state?.selectedSport ||
        params.get("sport") ||
        eventData.selectedSport;
      if (canonical.includes("Small")) {
        sport = "Indoor Soccer";
      }

      let players = location.state?.players;
      if (players == null) {
        if (canonical.includes("Small")) players = 10;
        else if (canonical.includes("All-Star")) players = 15;
        else if (canonical.includes("Big")) players = 20;
        else players = 26;
      } else {
        players = canonical.includes("Small")
          ? Math.min(Number(players), 12)
          : Number(players);
      }

      setEventData((prev) => ({
        ...prev,
        selectedPackage: canonical,
        selectedSport: sport,
        participants: players,
      }));
    }

    if (location.state?.selectedAddons) {
      setSelectedAddons(location.state.selectedAddons);
    }
    if (location.state?.selectedFood) {
      setSelectedFood(location.state.selectedFood);
    }
  }, [location.state, location.search]);

  // Initialize Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#050733";
  }, []);

  // Signature Drawing Handlers (Supports Mouse and Mobile Touch)
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (e) => {
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Signee Details Change Handler
  const handleSigneeChange = (e) => {
    const { name, value } = e.target;
    setSigneeData((prev) => ({ ...prev, [name]: value }));
  };

  // Package Selection Click Handler
  const handlePackageSelect = (pkgName) => {
    let newParticipants = eventData.participants;
    let newSport = eventData.selectedSport;

    if (pkgName.includes("Small")) {
      // Small Squad Party: max 12 players, soccer only
      const current = parseInt(newParticipants, 10);
      if (isNaN(current) || current > 12) {
        newParticipants = 10;
      }
      newSport = "Indoor Soccer";
    } else if (pkgName.includes("All-Star")) {
      // All-Star Squad Party: max 18 players
      const current = parseInt(newParticipants, 10);
      if (isNaN(current) || current > 18 || current <= 12) {
        newParticipants = 15;
      }
    } else if (pkgName.includes("Big League")) {
      // Big League Party: max 25 players
      const current = parseInt(newParticipants, 10);
      if (isNaN(current) || current > 25 || current <= 18) {
        newParticipants = 20;
      }
    } else if (pkgName.includes("Large Group")) {
      // Large Group (25+)
      const current = parseInt(newParticipants, 10);
      if (isNaN(current) || current <= 25) {
        newParticipants = 26;
      }
    }

    setEventData((prev) => ({
      ...prev,
      selectedPackage: pkgName,
      selectedSport: newSport,
      participants: newParticipants,
    }));
  };

  // Event Details Change Handler
  const handleEventChange = (e) => {
    const { name, value } = e.target;

    if (name === "participants") {
      const count = parseInt(value, 10);
      let updatedPackage = eventData.selectedPackage;
      let updatedSport = eventData.selectedSport;

      if (!isNaN(count)) {
        if (count > 25) {
          updatedPackage = "Large Group (25+)";
        } else if (count > 18) {
          updatedPackage = "Big League Party";
        } else if (count > 12) {
          updatedPackage = "All-Star Squad Party";
        } else if (count <= 12) {
          // If already on Small Squad, keep on Small Squad
          if (eventData.selectedPackage.includes("Small")) {
            updatedPackage = "Small Squad Party";
            updatedSport = "Indoor Soccer";
          }
        }
      }

      // If package is Small Squad Party, sport must always be Indoor Soccer
      if (updatedPackage.includes("Small")) {
        updatedSport = "Indoor Soccer";
      }

      setEventData((prev) => ({
        ...prev,
        participants: value,
        selectedPackage: updatedPackage,
        selectedSport: updatedSport,
      }));
      return;
    }

    setEventData((prev) => ({ ...prev, [name]: value }));
  };

  // Dependant Details Handlers
  const handleDependantChange = (index, field, value) => {
    setDependants((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addDependant = () => {
    setDependants((prev) => [
      ...prev,
      {
        firstName: "",
        lastName: "",
        email: "",
        mobilePhone: "",
        dob: "",
        addressLine1: signeeData.addressLine1 || "",
        addressLine2: signeeData.addressLine2 || "",
        city: signeeData.city || "",
        state: signeeData.state || "GA",
        zipCode: signeeData.zipCode || "",
      },
    ]);
  };

  const removeDependant = (index) => {
    if (dependants.length <= 1) return;
    setDependants((prev) => prev.filter((_, i) => i !== index));
  };

  // Toggle Add-on
  const toggleAddon = (item) => {
    setSelectedAddons((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item],
    );
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!signeeData.firstName.trim() || !signeeData.lastName.trim()) {
      toast.error("Please provide first and last name for the primary signee.");
      return;
    }
    if (!signeeData.email.trim() || !signeeData.mobilePhone.trim()) {
      toast.error("Please provide email and phone number.");
      return;
    }
    if (!signeeData.dob.trim()) {
      toast.error("Date of Birth is required for the signee of this form.");
      return;
    }
    if (!eventData.eventDate.trim()) {
      toast.error("Please choose a requested event date.");
      return;
    }

    if (signeeType === "adult_with_dependants") {
      for (let i = 0; i < dependants.length; i++) {
        if (!dependants[i].firstName.trim() || !dependants[i].lastName.trim()) {
          toast.error(
            `Please provide first and last name for Dependant / Child #${i + 1}.`,
          );
          return;
        }
      }
    }

    if (!agreedTerms) {
      toast.error(
        "Please review and accept the Strikes & Shots Terms & Conditions.",
      );
      return;
    }
    if (!agreedWaiver) {
      toast.error(
        "Please review and accept the Strikes & Shots Waiver & Release of Liability.",
      );
      return;
    }
    if (!electronicConsent) {
      toast.error("Please check the 'I give Electronic Consent' box to sign.");
      return;
    }
    if (!hasSignature) {
      toast.error(
        "Please draw your electronic signature in the signature box.",
      );
      return;
    }

    try {
      setSubmitting(true);

      const dependantsSummary =
        signeeType === "adult_with_dependants"
          ? dependants
              .map(
                (d, idx) =>
                  `  Child #${idx + 1}: ${d.firstName} ${d.lastName} (DOB: ${d.dob || "N/A"})`,
              )
              .join("\n")
          : "None (Adult only)";

      const messageContent = `[SPORTS PARTY & WAIVER SUBMISSION]
Portal: Strikes & Shots Official Registration Flow
Signee Type: ${signeeType === "adult" ? "Adult" : "Adult with Dependants"}
Attendance: ${
        signeeType === "adult"
          ? "Attending"
          : attendanceOption === "both"
            ? "Adult + Dependants attending"
            : "Only dependants attending"
      }
Primary Signee: ${signeeData.firstName} ${signeeData.lastName}
DOB: ${signeeData.dob}
Email: ${signeeData.email}
Phone: ${signeeData.mobilePhone}
Address: ${signeeData.addressLine1} ${signeeData.addressLine2 || ""}, ${signeeData.city}, ${signeeData.state} ${signeeData.zipCode}

--- Dependants / Children ---
${dependantsSummary}

--- Event & Package Details ---
Package: ${eventData.selectedPackage}
Sport: ${eventData.selectedSport}
Requested Date: ${eventData.eventDate}
Requested Time Slot: ${eventData.timeSlot}
Participants Count: ${eventData.participants}
Guest of Honor: ${eventData.celebrantName || "N/A"}${
        eventData.celebrantAge
          ? ` (Turning Age: ${eventData.celebrantAge})`
          : ""
      }
Add-ons: ${selectedAddons.length > 0 ? selectedAddons.join(", ") : "None"}
Food Options: ${selectedFood.length > 0 ? selectedFood.join(", ") : "None"}
Special Notes: ${eventData.notes || "None"}

--- Legal Verification ---
Terms & Conditions Accepted: Yes
Waiver & Release of Liability Accepted: Yes
Electronic Consent: Yes
Digital Signature Verified: Yes
Timestamp: ${new Date().toLocaleString()}`;

      // Capture canvas digital signature as Base64 image
      const signatureData = canvasRef.current
        ? canvasRef.current.toDataURL("image/png")
        : "";

      const bookingPayload = {
        signeeType,
        attendanceOption: signeeType === "adult" ? "self" : attendanceOption,
        primarySignee: {
          firstName: signeeData.firstName,
          lastName: signeeData.lastName,
          email: signeeData.email,
          mobilePhone: signeeData.mobilePhone,
          dob: signeeData.dob,
          addressLine1: signeeData.addressLine1,
          addressLine2: signeeData.addressLine2,
          city: signeeData.city,
          state: signeeData.state,
          zipCode: signeeData.zipCode,
        },
        dependants:
          signeeType === "adult_with_dependants"
            ? dependants.map((d) => ({
                firstName: d.firstName,
                lastName: d.lastName,
                dob: d.dob,
                mobilePhone: d.mobilePhone,
                addressLine1: d.addressLine1 || signeeData.addressLine1,
                addressLine2: d.addressLine2 || signeeData.addressLine2,
                city: d.city || signeeData.city,
                state: d.state || signeeData.state,
                zipCode: d.zipCode || signeeData.zipCode,
              }))
            : [],
        packageDetails: {
          packageName: eventData.selectedPackage,
          sport: eventData.selectedSport,
          eventDate: eventData.eventDate,
          timeSlot: eventData.timeSlot,
          participantsCount: Number(eventData.participants) || 10,
          celebrantName: eventData.celebrantName,
          celebrantAge: eventData.celebrantAge,
          addons: selectedAddons,
          foodSelections: selectedFood,
          notes: eventData.notes,
        },
        legalConsent: {
          agreedTerms,
          agreedWaiver,
          electronicConsent,
          signatureData,
        },
      };

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/party-request/create`,
          bookingPayload,
        );
        if (res.data?.bookingReference) {
          setBookingReference(res.data.bookingReference);
        }
      } catch (apiErr) {
        console.warn("Primary booking API fallback to contact API:", apiErr);
        // Fallback to contacts API
        const legacyMessage = `[SPORTS PARTY & WAIVER SUBMISSION]
Primary Signee: ${signeeData.firstName} ${signeeData.lastName} (${signeeData.email}, ${signeeData.mobilePhone})
Package: ${eventData.selectedPackage} | Sport: ${eventData.selectedSport}
Date: ${eventData.eventDate} (${eventData.timeSlot}) | Players: ${eventData.participants}
Celebrant: ${eventData.celebrantName || "N/A"}
Addons: ${selectedAddons.join(", ") || "None"}
Agreed: Terms & Waiver Signed`;

        await axios.post(`${process.env.REACT_APP_API_URL}/contacts/create`, {
          firstName: signeeData.firstName,
          lastName: signeeData.lastName,
          email: signeeData.email,
          mobileNo: signeeData.mobilePhone,
          message: legacyMessage,
        });
      }

      setSubmitted(true);
      toast.success(
        "Party Request & Signed Waiver Received! We will contact you promptly.",
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error submitting party request", err);
      toast.error("Failed to submit request. Please try again or call us!");
    } finally {
      setSubmitting(false);
    }
  };

  const packageOptions = useMemo(() => {
    if (
      activeDbPackages &&
      Array.isArray(activeDbPackages) &&
      activeDbPackages.length > 0
    ) {
      const dbOptions = activeDbPackages
        .filter((pkg) => pkg.isActive !== false)
        .map((pkg) => {
          let id = "small";
          if (pkg.slug?.includes("all-star") || pkg.slug?.includes("allstar"))
            id = "allstar";
          else if (pkg.slug?.includes("big")) id = "bigleague";
          else if (pkg.slug?.includes("small")) id = "small";
          else id = pkg.slug || pkg._id;

          return {
            id,
            name: pkg.name,
            price: `$${pkg.price}`,
            players:
              pkg.partySize || `Max ${pkg.maxParticipants || 12} players`,
            sport: pkg.sportChoice || "Choose 1 Major Sport",
            popular: Boolean(pkg.isPopular),
          };
        });

      dbOptions.push({
        id: "custom",
        name: "Large Group (25+)",
        price: "Custom",
        players: "25+ participants",
        sport: "Multi-court & Custom",
      });

      return dbOptions;
    }

    return [
      {
        id: "small",
        name: "Small Squad Party",
        price: "$275",
        players: "Max 12 players",
        sport: "Soccer Only",
      },
      {
        id: "allstar",
        name: "All-Star Squad Party",
        price: "$375",
        players: "Max 18 players",
        sport: "Choose 1 Major Sport",
        popular: true,
      },
      {
        id: "bigleague",
        name: "Big League Party",
        price: "$475",
        players: "Max 25 players",
        sport: "Choose 1 Major Sport",
      },
      {
        id: "custom",
        name: "Large Group (25+)",
        price: "Custom",
        players: "25+ participants",
        sport: "Multi-court & Custom",
      },
    ];
  }, [activeDbPackages]);

  // If the currently selected package is no longer active in packageOptions, switch to the first active package
  useEffect(() => {
    if (packageOptions && packageOptions.length > 0) {
      const isValid = packageOptions.some(
        (opt) =>
          opt.name.toLowerCase() ===
          (eventData.selectedPackage || "").toLowerCase(),
      );
      if (!isValid) {
        setEventData((prev) => ({
          ...prev,
          selectedPackage: packageOptions[0].name,
        }));
      }
    }
  }, [packageOptions, eventData.selectedPackage]);

  const selectedPkgLower = (eventData.selectedPackage || "").toLowerCase();
  const isSmallSquad = selectedPkgLower.includes("small");
  const isAllStar =
    selectedPkgLower.includes("all-star") ||
    selectedPkgLower.includes("all star") ||
    selectedPkgLower.includes("allstar");
  const isBigLeague =
    selectedPkgLower.includes("big") || selectedPkgLower.includes("league");
  const isLargeGroup =
    selectedPkgLower.includes("large") || selectedPkgLower.includes("25");

  // Agreement active state: all checkboxes must be ticked to enable submission
  const isConsentAgreed = Boolean(
    agreedTerms && agreedWaiver && electronicConsent,
  );

  return (
    <div className="w-full bg-[#f8f9fc] text-gray-800 select-none">
      <PageBanner
        title="REQUEST A SPORTS PARTY"
        subtitle="Official Party Reservation, Participant Registration & Waiver Portal"
        background={bannerBg}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10 sm:py-16">
        {submitted ? (
          /* Confirmation State */
          <div className="bg-white border-2 border-emerald-500/50 rounded-2xl p-8 sm:p-12 shadow-xl text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-5">
              <FaCheckCircle />
            </div>
            <h2 className="text-3xl font-heading font-black text-primaryBtnColor uppercase tracking-wide">
              Party Request &amp; Waiver Submitted!
            </h2>
            <p className="mt-4 text-gray-600 font-subheading text-base leading-relaxed max-w-xl mx-auto">
              Thank you,{" "}
              <strong className="text-gray-900">{signeeData.firstName}</strong>!
              Your party request for{" "}
              <strong className="text-primaryBtnHoverColor">
                {eventData.selectedPackage}
              </strong>{" "}
              and signed waiver documents have been officially received.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mt-8 text-left text-sm space-y-2 font-subheading max-w-lg mx-auto">
              {bookingReference && (
                <div className="flex justify-between items-center py-1.5 border-b border-gray-200 bg-red-50/50 -mx-6 px-6 -mt-6 pt-5 rounded-t-xl mb-2">
                  <span className="text-gray-600 font-bold">
                    Booking Reference:
                  </span>
                  <span className="font-heading font-black text-primaryBtnHoverColor tracking-wider text-sm sm:text-base">
                    {bookingReference}
                  </span>
                </div>
              )}
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-gray-500">Signee:</span>
                <span className="font-bold text-gray-900">
                  {signeeData.firstName} {signeeData.lastName}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-gray-500">Event Date:</span>
                <span className="font-bold text-gray-900">
                  {eventData.eventDate} ({eventData.timeSlot})
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-gray-500">Sport:</span>
                <span className="font-bold text-gray-900">
                  {eventData.selectedSport}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-gray-500">Participants:</span>
                <span className="font-bold text-gray-900">
                  {eventData.participants} players
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Waiver Status:</span>
                <span className="font-bold text-emerald-600 flex items-center gap-1">
                  <FaCheckCircle className="text-xs" /> Signed &amp; Verified
                </span>
              </div>
            </div>

            <p className="mt-6 text-xs text-gray-500 font-subheading">
              Our events coordinator will reach out promptly to confirm court
              availability and provide deposit details.
            </p>

            <div className="mt-8 flex justify-center gap-4">
              <button
                onClick={() => navigate("/event-packages")}
                className="bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white text-xs sm:text-sm font-bold px-6 py-3 rounded-lg transition-colors cursor-pointer uppercase tracking-wider"
              >
                Back to Event Packages
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* 1. Facility Header Card (Clubworx Style) */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-heading font-black text-primaryBtnColor tracking-tight">
                  Strikes &amp; Shots LLC
                </h1>
                <p className="text-xs text-gray-500 font-subheading mt-1">
                  Sports Party Booking &amp; Participant Registration Portal
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-600 font-subheading">
                  <FaMapMarkerAlt className="text-primaryBtnHoverColor flex-shrink-0" />
                  <span>
                    4285 Brogdon Exchange, Suwanee, Georgia, 30024, US
                  </span>
                </div>
              </div>

              <div className="text-xs sm:text-sm font-subheading text-gray-600 space-y-1.5 sm:text-right border-t sm:border-t-0 pt-4 sm:pt-0 w-full sm:w-auto">
                <p className="flex items-center sm:justify-end gap-2">
                  <FaPhoneAlt className="text-primaryBtnHoverColor text-xs" />
                  <a
                    href="tel:4705132256"
                    className="font-bold text-gray-900 hover:text-primaryBtnHoverColor transition-colors"
                  >
                    470-513-2256 (Cell)
                  </a>
                </p>
                <p className="flex items-center sm:justify-end gap-2">
                  <FaPhoneAlt className="text-primaryBtnHoverColor text-xs" />
                  <a
                    href="tel:6787147454"
                    className="font-bold text-gray-900 hover:text-primaryBtnHoverColor transition-colors"
                  >
                    678-714-7454 (Office)
                  </a>
                </p>
                <p className="flex items-center sm:justify-end gap-2">
                  <FaEnvelope className="text-primaryBtnHoverColor text-xs" />
                  <a
                    href="mailto:spectrasports25@gmail.com"
                    className="text-gray-700 hover:text-primaryBtnHoverColor transition-colors"
                  >
                    spectrasports25@gmail.com
                  </a>
                </p>
              </div>
            </div>

            {/* 2. Signee Type Selector: Adult vs Adult with Dependants */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-xs">
              <h2 className="text-base sm:text-lg font-heading font-bold text-gray-900 mb-2 uppercase tracking-wide">
                Who is signing this party registration?
              </h2>
              <p className="text-xs text-gray-500 font-subheading mb-5">
                Select your registrant type. Parents booking youth birthday
                parties or child teams should choose "Adult with Dependants".
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label
                  onClick={() => setSigneeType("adult")}
                  className={`p-5 rounded-xl border-2 flex items-start gap-4 cursor-pointer transition-all duration-200 ${
                    signeeType === "adult"
                      ? "border-primaryBtnHoverColor bg-red-50/20 shadow-xs"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="signeeType"
                    checked={signeeType === "adult"}
                    onChange={() => setSigneeType("adult")}
                    className="mt-1 accent-primaryBtnHoverColor"
                  />
                  <div>
                    <div className="flex items-center gap-2 font-heading font-bold text-sm sm:text-base text-gray-900">
                      <FaUser className="text-primaryBtnHoverColor text-sm" />
                      <span>Adult</span>
                    </div>
                    <p className="text-xs text-gray-500 font-subheading mt-1 leading-relaxed">
                      Signing for myself or an adult group / team celebration.
                    </p>
                  </div>
                </label>

                <label
                  onClick={() => setSigneeType("adult_with_dependants")}
                  className={`p-5 rounded-xl border-2 flex items-start gap-4 cursor-pointer transition-all duration-200 ${
                    signeeType === "adult_with_dependants"
                      ? "border-primaryBtnHoverColor bg-red-50/20 shadow-xs"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="signeeType"
                    checked={signeeType === "adult_with_dependants"}
                    onChange={() => setSigneeType("adult_with_dependants")}
                    className="mt-1 accent-primaryBtnHoverColor"
                  />
                  <div>
                    <div className="flex items-center gap-2 font-heading font-bold text-sm sm:text-base text-gray-900">
                      <FaUserFriends className="text-primaryBtnHoverColor text-sm" />
                      <span>Adult with Dependants</span>
                    </div>
                    <p className="text-xs text-gray-500 font-subheading mt-1 leading-relaxed">
                      Signing as a parent or legal guardian for child
                      participants / minors.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* 3. Primary Signee / Member Details */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <div className="bg-gray-100/80 px-6 py-4 border-b border-gray-200">
                <h3 className="font-heading font-bold text-sm sm:text-base text-gray-900 uppercase tracking-wide">
                  Member / Primary Signee Details
                </h3>
              </div>

              <div className="p-6 sm:p-8 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={signeeData.firstName}
                      onChange={handleSigneeChange}
                      placeholder="First Name (required)"
                      required
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={signeeData.lastName}
                      onChange={handleSigneeChange}
                      placeholder="Last Name (required)"
                      required
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={signeeData.email}
                      onChange={handleSigneeChange}
                      placeholder="Email (required)"
                      required
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Mobile Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobilePhone"
                      value={signeeData.mobilePhone}
                      onChange={handleSigneeChange}
                      placeholder="Mobile Phone (required)"
                      required
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={signeeData.dob}
                      onChange={handleSigneeChange}
                      required
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div className="pt-2 sm:pt-5">
                    <span className="text-xs text-gray-500 font-subheading italic">
                      ℹ️ DOB is required for the signee of this form.
                    </span>
                  </div>
                </div>

                {/* Address Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={signeeData.addressLine1}
                      onChange={handleSigneeChange}
                      placeholder="Address Line 1"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={signeeData.addressLine2}
                      onChange={handleSigneeChange}
                      placeholder="Address Line 2 (Apt, Suite, etc.)"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={signeeData.city}
                      onChange={handleSigneeChange}
                      placeholder="City"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      State / Province
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={signeeData.state}
                      onChange={handleSigneeChange}
                      placeholder="State"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Zip / Post Code
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={signeeData.zipCode}
                      onChange={handleSigneeChange}
                      placeholder="Zip Code"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Dependants / Children Section (Shown if Adult with Dependants is selected) */}
            {signeeType === "adult_with_dependants" && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs space-y-6 p-6 sm:p-8">
                <div>
                  <h3 className="font-heading font-bold text-base text-gray-900 uppercase tracking-wide mb-3">
                    Attendance Option
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 text-xs sm:text-sm font-subheading">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="radio"
                        name="attendanceOption"
                        checked={attendanceOption === "both"}
                        onChange={() => setAttendanceOption("both")}
                        className="accent-primaryBtnHoverColor"
                      />
                      <span>
                        I'll be attending, as well as my dependants / children
                      </span>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="radio"
                        name="attendanceOption"
                        checked={attendanceOption === "dependants_only"}
                        onChange={() => setAttendanceOption("dependants_only")}
                        className="accent-primaryBtnHoverColor"
                      />
                      <span>
                        Only my dependants / children will be attending
                      </span>
                    </label>
                  </div>
                </div>

                <div className="space-y-6 pt-4 border-t border-gray-200">
                  {dependants.map((dep, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50/80 border border-gray-200 rounded-xl p-5 sm:p-6 space-y-4"
                    >
                      <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                        <span className="font-heading font-bold text-sm sm:text-base text-primaryBtnColor uppercase">
                          Dependant / Child #{idx + 1}
                        </span>
                        {dependants.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDependant(idx)}
                            className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 font-bold cursor-pointer"
                          >
                            <FaTrash className="text-xs" /> Remove
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={dep.firstName}
                            onChange={(e) =>
                              handleDependantChange(
                                idx,
                                "firstName",
                                e.target.value,
                              )
                            }
                            placeholder="Child's First Name"
                            required
                            className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={dep.lastName}
                            onChange={(e) =>
                              handleDependantChange(
                                idx,
                                "lastName",
                                e.target.value,
                              )
                            }
                            placeholder="Child's Last Name"
                            required
                            className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                            Date of Birth
                          </label>
                          <input
                            type="date"
                            value={dep.dob}
                            onChange={(e) =>
                              handleDependantChange(idx, "dob", e.target.value)
                            }
                            className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                            Child's Phone / Parent Cell
                          </label>
                          <input
                            type="tel"
                            value={dep.mobilePhone}
                            onChange={(e) =>
                              handleDependantChange(
                                idx,
                                "mobilePhone",
                                e.target.value,
                              )
                            }
                            placeholder="Mobile Phone"
                            className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addDependant}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-lg transition-colors cursor-pointer border border-gray-300"
                  >
                    <FaPlus className="text-xs" />
                    <span>Add another dependant / child</span>
                  </button>
                </div>
              </div>
            )}

            {/* 5. Party & Package Configuration */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <div className="bg-gray-100/80 px-6 py-4 border-b border-gray-200">
                <h3 className="font-heading font-bold text-sm sm:text-base text-gray-900 uppercase tracking-wide">
                  Select Sports Party Package &amp; Event Details
                </h3>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                {/* Package Options Grid */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-3">
                    Choose Your Package <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {packageOptions.map((pkg) => {
                      const isSelected =
                        resolveCanonicalPackage(eventData.selectedPackage) ===
                        pkg.name;
                      return (
                        <div
                          key={pkg.id}
                          onClick={() => handlePackageSelect(pkg.name)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                            isSelected
                              ? "border-primaryBtnHoverColor bg-red-50/20 shadow-xs"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          {pkg.popular && (
                            <span className="absolute -top-2.5 right-3 bg-primaryBtnHoverColor text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shadow-2xs">
                              Popular
                            </span>
                          )}
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-heading font-bold text-xs sm:text-sm text-gray-900">
                              {pkg.name}
                            </span>
                          </div>
                          <div className="text-lg font-black text-primaryBtnColor">
                            {pkg.price}
                          </div>
                          <div className="text-[11px] text-gray-500 font-subheading mt-1">
                            {pkg.players}
                          </div>
                          <div className="text-[10px] text-gray-400 font-subheading mt-0.5">
                            {pkg.sport}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Sport, Date, Time & Participants */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Primary Sport <span className="text-red-500">*</span>
                    </label>
                    {isSmallSquad ? (
                      <select
                        name="selectedSport"
                        value="Indoor Soccer"
                        onChange={handleEventChange}
                        className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
                      >
                        <option value="Indoor Soccer">⚽ Indoor Soccer</option>
                      </select>
                    ) : (
                      <select
                        name="selectedSport"
                        value={
                          eventData.selectedSport.includes("Dual")
                            ? "Indoor Soccer"
                            : eventData.selectedSport
                        }
                        onChange={handleEventChange}
                        className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
                      >
                        <option value="Indoor Soccer">⚽ Indoor Soccer</option>
                        <option value="Basketball">🏀 Basketball</option>
                        <option value="Volleyball">🏐 Volleyball</option>
                        <option value="Indoor Cricket">
                          🏏 Indoor Cricket
                        </option>
                      </select>
                    )}
                    <span className="text-[10px] text-gray-500 font-subheading mt-1 block">
                      {isSmallSquad
                        ? "Exclusive use of Small Soccer Field"
                        : "Dedicated field/court for selected sport"}
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Requested Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="eventDate"
                      value={eventData.eventDate}
                      onChange={handleEventChange}
                      required
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Preferred Time Slot
                    </label>
                    <select
                      name="timeSlot"
                      value={eventData.timeSlot}
                      onChange={handleEventChange}
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
                    >
                      <option value="10:00 AM - 12:30 PM">
                        10:00 AM – 12:30 PM
                      </option>
                      <option value="1:00 PM - 3:30 PM">
                        1:00 PM – 3:30 PM
                      </option>
                      <option value="4:00 PM - 6:30 PM">
                        4:00 PM – 6:30 PM
                      </option>
                      <option value="7:00 PM - 9:30 PM">
                        7:00 PM – 9:30 PM
                      </option>
                      <option value="Custom Time Window">
                        Custom Time Window
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Estimated Players <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="participants"
                      min="1"
                      value={eventData.participants}
                      onChange={handleEventChange}
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                    <span className="text-[10px] text-gray-500 font-subheading mt-1 block leading-tight">
                      {isSmallSquad
                        ? "Max 12 players (10 included + 2 add-on; switches to All-Star above 12)"
                        : isAllStar
                          ? "Max 18 players (15 included + 3 add-on; switches to Big League above 18)"
                          : isBigLeague
                            ? "Max 25 players (20 included + 5 add-on; switches to Large Group above 25)"
                            : "25+ players (Custom package & arena buyout)"}
                    </span>
                  </div>
                </div>

                {/* Celebrant / Guest of Honor */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Guest of Honor / Birthday Star Name (Optional)
                    </label>
                    <input
                      type="text"
                      name="celebrantName"
                      value={eventData.celebrantName}
                      onChange={handleEventChange}
                      placeholder="e.g. Leo, Maya"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                      Turning Age (Optional)
                    </label>
                    <input
                      type="text"
                      name="celebrantAge"
                      value={eventData.celebrantAge}
                      onChange={handleEventChange}
                      placeholder="e.g. 10th Birthday"
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                  </div>
                </div>

                {/* Custom Add-ons Checkboxes */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                    Add-ons &amp; Customizations
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs font-subheading">
                    {[
                      "Extra Participants Add-on",
                      "Fresh Artisan Pizza Package",
                      "Fountain Beverages Station",
                      "Custom Playlist & Referee",
                      "Arena Buyout / Dual Court",
                    ].map((addon, idx) => (
                      <label
                        key={idx}
                        className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 bg-gray-50/60 hover:bg-white cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAddons.includes(addon)}
                          onChange={() => toggleAddon(addon)}
                          className="accent-primaryBtnHoverColor"
                        />
                        <span className="text-gray-700">{addon}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Special Requests or Notes
                  </label>
                  <textarea
                    name="notes"
                    rows="2"
                    value={eventData.notes}
                    onChange={handleEventChange}
                    placeholder="Tell us about any specific court preferences, setup requests, or schedule flexibility..."
                    className="w-full px-3.5 py-2.5 bg-gray-50/50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:border-primaryBtnHoverColor"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* 6. Strikes & Shots LLC Terms & Conditions (Scrollable Box) */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <div className="bg-gray-100/80 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-heading font-bold text-sm sm:text-base text-gray-900 uppercase tracking-wide flex items-center gap-2">
                  <FaFileContract className="text-primaryBtnHoverColor" />
                  <span>Strikes &amp; Shots LLC Terms &amp; Conditions</span>
                </h3>
                <span className="text-[11px] text-gray-500 font-subheading italic">
                  Scroll to review full text
                </span>
              </div>

              <div className="p-6">
                <div className="h-56 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-subheading text-gray-700 space-y-3 leading-relaxed">
                  <h4 className="font-heading font-bold text-gray-900 text-sm">
                    General Facility Rules
                  </h4>
                  <p>
                    1. <strong>Mandatory Check-In:</strong> All guests and
                    participants must check in at the front desk before
                    accessing any playing field, court, or social area.
                  </p>
                  <p>
                    2. <strong>Sport-Specific Footwear:</strong> All
                    participants must wear clean, non-marking athletic shoes on
                    courts and turf surfaces. Cleats with metal spikes and
                    street shoes are strictly prohibited.
                  </p>
                  <p>
                    3. <strong>Court Safety &amp; Conduct:</strong> Strikes
                    &amp; Shots LLC maintains a zero-tolerance policy for
                    aggressive, reckless, or unsportsmanlike behavior.
                    Management reserves the right to remove any individual who
                    endangers staff or other guests.
                  </p>
                  <p>
                    4. <strong>Food &amp; Drinks Policy:</strong> No outside
                    food, beverages, chewing gum, or glass containers are
                    permitted on the playing surfaces or turf areas. Food and
                    drinks must remain confined to designated party and social
                    seating areas.
                  </p>
                  <p>
                    5. <strong>Prohibited Items:</strong> Alcohol, weapons,
                    smoking, vaping, and pets (with the exception of ADA
                    certified service animals) are strictly prohibited on
                    premises. Violations may result in immediate expulsion and
                    statutory fines.
                  </p>

                  <h4 className="font-heading font-bold text-gray-900 text-sm pt-2">
                    Court Rentals &amp; Event Booking Policies
                  </h4>
                  <p>
                    1. <strong>48-Hour Full Refund Policy:</strong>{" "}
                    Cancellations made in writing at least 48 hours prior to the
                    scheduled start time will receive a 100% refund.
                  </p>
                  <p>
                    2. <strong>24–48 Hour Window:</strong> Cancellations made
                    between 24 and 48 hours prior to the event are subject to a
                    50% cancellation fee at management discretion.
                  </p>
                  <p>
                    3. <strong>Less than 24 Hours:</strong> Cancellations made
                    under 24 hours prior to the event or no-shows are completely
                    non-refundable.
                  </p>
                  <p>
                    4. <strong>Written Notice:</strong> All cancellation or
                    reschedule requests must be submitted in writing via email
                    to <em>spectrasports25@gmail.com</em> or{" "}
                    <em>info@strikesandshots.com</em>.
                  </p>

                  <h4 className="font-heading font-bold text-gray-900 text-sm pt-2">
                    Lost &amp; Found &amp; Personal Belongings
                  </h4>
                  <p>
                    Strikes &amp; Shots LLC is not responsible for lost, stolen,
                    or damaged personal property. Any items placed in Lost &amp;
                    Found and unclaimed after 14 days will be donated or
                    discarded.
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="termsAgreement"
                    checked={agreedTerms}
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    className="w-4 h-4 accent-primaryBtnHoverColor cursor-pointer"
                  />
                  <label
                    htmlFor="termsAgreement"
                    className="text-xs sm:text-sm font-heading font-bold text-gray-900 cursor-pointer"
                  >
                    I have read and agree to the Strikes &amp; Shots LLC Terms
                    &amp; Conditions <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 7. Strikes & Shots LLC Waiver & Release of Liability (Scrollable Box) */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <div className="bg-gray-100/80 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-heading font-bold text-sm sm:text-base text-gray-900 uppercase tracking-wide flex items-center gap-2">
                  <FaShieldAlt className="text-primaryBtnHoverColor" />
                  <span>
                    Strikes &amp; Shots LLC Waiver &amp; Release of Liability
                  </span>
                </h3>
                <span className="text-[11px] text-gray-500 font-subheading italic">
                  Scroll to review full text
                </span>
              </div>

              <div className="p-6">
                <div className="h-60 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-subheading text-gray-700 space-y-3 leading-relaxed">
                  <h4 className="font-heading font-bold text-gray-900 text-sm">
                    Acknowledgment of Risks &amp; Voluntary Participation
                  </h4>
                  <p>
                    I, on behalf of myself and/or my participating minor
                    child/dependants, hereby acknowledge that participation in
                    athletic activities, sports party games, soccer, basketball,
                    volleyball, cricket, and related events involves inherent
                    risks of physical injury, including but not limited to
                    sprains, fractures, concussions, collision with other
                    players, contact with facility equipment, and overexertion.
                  </p>
                  <p>
                    I understand that these risks cannot be eliminated without
                    compromising the essential nature of the sport and activity.
                    I voluntarily choose to participate and/or give permission
                    for my minor child/dependants to participate with full
                    knowledge and understanding of these risks.
                  </p>

                  <h4 className="font-heading font-bold text-gray-900 text-sm pt-2">
                    Release of Liability &amp; Indemnification
                  </h4>
                  <p>
                    In consideration of being permitted to enter and use the
                    facilities and equipment of Strikes &amp; Shots LLC, I
                    hereby release, waive, discharge, and hold harmless Strikes
                    &amp; Shots LLC, its owners, directors, officers, coaches,
                    referees, employees, agents, and affiliated entities from
                    any and all liability, claims, demands, or causes of action
                    arising out of negligence, personal injury, property loss,
                    or damage occurring during our presence on premises.
                  </p>

                  <h4 className="font-heading font-bold text-gray-900 text-sm pt-2">
                    Medical &amp; Insurance Representation
                  </h4>
                  <p>
                    I certify that I and/or my child are in good physical health
                    and possess no known medical condition that would make
                    participation unsafe. I confirm that we carry adequate
                    health, medical, or accidental insurance to cover any injury
                    sustained while participating, or accept full financial
                    responsibility for all medical costs.
                  </p>

                  <h4 className="font-heading font-bold text-gray-900 text-sm pt-2">
                    Media &amp; Photographic Release
                  </h4>
                  <p>
                    I grant Strikes &amp; Shots LLC the irrevocable right and
                    permission to take photographs and/or video recordings of
                    participants during party events for promotional, website,
                    and social media materials without compensation or
                    inspection rights.
                  </p>

                  <h4 className="font-heading font-bold text-gray-900 text-sm pt-2">
                    Parental &amp; Guardian Consent for Minors
                  </h4>
                  <p>
                    If signing as a parent or legal guardian of minor
                    participant(s), I warrant that I have legal authority to
                    sign this release on behalf of the minor(s). I agree to
                    indemnify and hold harmless Strikes &amp; Shots LLC from any
                    claims brought by or on behalf of said minor(s).
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="waiverAgreement"
                    checked={agreedWaiver}
                    onChange={(e) => setAgreedWaiver(e.target.checked)}
                    className="w-4 h-4 accent-primaryBtnHoverColor cursor-pointer"
                  />
                  <label
                    htmlFor="waiverAgreement"
                    className="text-xs sm:text-sm font-heading font-bold text-gray-900 cursor-pointer"
                  >
                    I have read, understood, and voluntarily agree to the Waiver
                    &amp; Release of Liability{" "}
                    <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 8. Digital Signature & Electronic Consent */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <div className="bg-gray-100/80 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-heading font-bold text-sm sm:text-base text-gray-900 uppercase tracking-wide">
                  Signee Digital Signature &amp; Electronic Consent
                </h3>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-xs font-bold text-gray-600 hover:text-red-600 flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <FaEraser className="text-xs" /> Clear signature
                </button>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                    Draw Your Signature in the box below{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50/50 hover:border-gray-400 transition-colors">
                    <canvas
                      ref={canvasRef}
                      width={1000}
                      height={180}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="w-full h-40 cursor-crosshair touch-none"
                    />
                    {!hasSignature && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400 text-xs sm:text-sm font-subheading">
                        ✍️ Sign with your finger or mouse here
                      </div>
                    )}
                  </div>
                </div>

                {/* Electronic Consent Checkbox */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="electronicConsent"
                    checked={electronicConsent}
                    onChange={(e) => setElectronicConsent(e.target.checked)}
                    required
                    className="w-5 h-5 mt-0.5 accent-primaryBtnHoverColor cursor-pointer flex-shrink-0"
                  />
                  <label
                    htmlFor="electronicConsent"
                    className="text-xs sm:text-sm font-subheading text-gray-800 cursor-pointer leading-relaxed"
                  >
                    <strong className="font-heading font-bold text-gray-900 block mb-0.5">
                      I give Electronic Consent{" "}
                      <span className="text-red-500">*</span>
                    </strong>
                    I certify that I am at least 18 years of age and legally
                    authorized to execute this agreement. I consent to using
                    electronic records and signatures.
                  </label>
                </div>

                {/* Submit Action */}
                <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs font-subheading text-center sm:text-left">
                    {!isConsentAgreed ? (
                      <p className="text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg inline-block">
                        ⚠️ Please review and check all agreement boxes above
                        (Terms &amp; Conditions, Waiver, and Electronic Consent)
                        to activate this button.
                      </p>
                    ) : (
                      <p className="text-gray-500">
                        All agreement checkboxes completed. Click below to
                        submit your party reservation and signed waiver.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !isConsentAgreed}
                    className={`w-full sm:w-auto font-heading font-bold text-sm px-8 py-3.5 rounded-lg uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 flex-shrink-0 ${
                      isConsentAgreed && !submitting
                        ? "bg-primaryBtnHoverColor hover:bg-red-800 text-white shadow-lg shadow-red-950/20 cursor-pointer active:scale-95"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300 shadow-none"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <FaSpinner className="animate-spin text-sm" />
                        <span>Submitting Request...</span>
                      </>
                    ) : (
                      <>
                        <span>Agree &amp; Submit Party Request</span>
                        <FaArrowRight className="text-xs" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PartyRequest;
